import { createContext, useContext } from "react";
import { Spinner } from "@salesapp/ui";
import { CURRENCY_SYMBOLS } from "../../../../lib/localeFormat";
import { useLocale } from "../../../../shell/LocaleProvider";
import type { DashboardChartItem } from "../api";
import { resolveSchemeColors } from "../chartColorSchemes";
import { Card } from "./UiCard";

type Row = { label: string; value: number };
type Series = { label: string; data: number[] };
type GaugeMeta = { actual?: number; target?: number; period_label?: string };

function prettyLabel(label: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(label)) {
    const parsed = new Date(`${label.slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
  }
  return label.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
}

function prettyValue(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${value < 0 ? "-" : ""}${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (abs >= 10_000) return `${value < 0 ? "-" : ""}${Math.round(abs / 1000)}k`;
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 10) / 10);
}

/** "$" on a saved chart is a money marker, drawn with the organisation currency. */
const OrgMoneySymbolContext = createContext("$");

function useOrgMoneySymbol(): string {
  return useContext(OrgMoneySymbolContext);
}

const MONEY_MARKS = new Set(Object.values(CURRENCY_SYMBOLS));

function isMoneyUnit(unit: string | null | undefined): boolean {
  if (!unit) return false;
  const trimmed = unit.trim();
  if (MONEY_MARKS.has(trimmed)) return true;
  return Object.prototype.hasOwnProperty.call(CURRENCY_SYMBOLS, trimmed.toUpperCase());
}

function prettyMoney(value: number, symbol: string): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const mark = symbol || "$";
  if (abs >= 1_000_000) return `${sign}${mark}${(abs / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
  if (abs >= 1_000) return `${sign}${mark}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return `${sign}${mark}${Math.round(abs).toLocaleString("en-US")}`;
}

function formatMarked(value: number, axisUnit: string | undefined, symbol: string): string {
  if (isMoneyUnit(axisUnit)) return prettyMoney(value, symbol);
  return `${prettyValue(value)}${axisUnit ? ` ${axisUnit}` : ""}`;
}

function orgMoneyLabel(text: string, symbol: string): string {
  if (!text || !symbol || symbol === "$") return text;
  return text.replace(/\$/g, symbol);
}

/**
 * Plain inline-SVG chart rendering, viewBox-relative so no hardcoded pixel
 * values are needed. No charting library added: full parity with
 * PlatformHub's own ~20-entry chart-type gallery (dashboard.tsx, which uses
 * Chart.js/chartjs-chart-wordcloud plus 4 hand-rolled DOM/SVG renderers --
 * word cloud, gauge, heatmap-grid, calendar) is reimplemented here as plain
 * inline SVG/DOM, matching this file's existing house style rather than
 * adding a charting-library dependency. Adapted from features/dashboards/
 * cards/ChartCard.tsx (copied rather than imported -- that package still
 * backs Risk & Control's untouched old dashboard).
 *
 * IMPORTANT DATA-MODEL CAVEAT (read before extending): this platform's chart
 * data query (dashboard_designer/service.py's chart-data endpoint) returns
 * only single-series {label, value}[] rows -- unlike PlatformHub's own
 * run_chart_query, which has a genuine multi-series pivot
 * ({labels, datasets: [{label, data}]}) for clustered/stacked/double-bar/
 * tornado/heatmap-grid. No such pivoted query shape exists here yet, so
 * every type below that PlatformHub renders as true multi-series is instead
 * an honest, documented single-series simplification -- see each function's
 * own comment for exactly what was simplified and why. Building the real
 * pivoted query is real backend scope, not something to fake client-side.
 *
 * Full-parity Configure panel fields wired into rendering here (see
 * docs/DASHBOARD_DESIGNER.md): titleColor tints the Card title, colorScheme
 * picks a named palette (theme tokens or built-in / Tableau / Okabe–Ito
 * schemes), orientation flips the plain "bar" type to horizontal bars (the
 * dedicated bar_horizontal type below always renders horizontal regardless
 * of this toggle), showLegend toggles a simple legend row, and showValues
 * prints the numeric value on/above each mark.
 */
export function ChartCard({ item, rows, categories, series, meta, loading }: {
  item: DashboardChartItem;
  rows: Row[];
  categories?: string[];
  series?: Series[];
  meta?: GaugeMeta;
  loading: boolean;
}) {
  const colors = resolveSchemeColors(item.colorScheme);
  const { orgCurrencySymbol } = useLocale();
  const moneySymbol = orgCurrencySymbol || "$";
  const showLegend = item.showLegend ?? true;
  const hasSeriesLegend = showLegend && (series?.length ?? 0) > 0;
  const hasRowLegend = showLegend && !hasSeriesLegend && rows.length > 0 && !NO_LEGEND_TYPES.has(item.chartType);
  return (
    <OrgMoneySymbolContext.Provider value={moneySymbol}>
    <div title={item.tooltip || undefined} className="dashboard-chart-card-wrap">
      <Card
        title={<span style={item.titleColor ? { color: item.titleColor } : undefined}>{orgMoneyLabel(item.title, moneySymbol)}</span>}
        className="dashboard-chart-card"
      >
        {item.fieldDisplayName && <p className="dashboard-chart-card__axis-title">{orgMoneyLabel(item.fieldDisplayName, moneySymbol)}</p>}
        {loading ? <Spinner /> : rows.length === 0 && !(series && series.length > 0) ? (
          <p className="dashboard-chart-card__empty">No data yet.</p>
        ) : (
          <div className={`dashboard-chart-plot dashboard-chart-plot--${item.chartType}`}>
            <ChartByType item={item} rows={rows} colors={colors} categories={categories} series={series} meta={meta} />
          </div>
        )}
        {hasSeriesLegend && series && <SeriesLegend series={series} colors={colors} />}
        {hasRowLegend && <ChartLegend rows={rows} colors={colors} />}
      </Card>
    </div>
    </OrgMoneySymbolContext.Provider>
  );
}

/** Types that render their own legend-equivalent inline (word cloud/gauge/
 * sparkline/number_trend have no per-row colour-coded marks a legend would
 * describe), or where a legend would be actively misleading given the
 * single-series simplification (calendar/heatmap -- one colour ramp, not
 * discrete series). */
const NO_LEGEND_TYPES = new Set<DashboardChartItem["chartType"]>([
  "number_trend", "sparkline", "gauge", "word_cloud", "calendar", "heatmap_bar", "heatmap_grid", "funnel",
]);

function ChartByType({ item, rows, colors, categories, series, meta }: {
  item: DashboardChartItem;
  rows: Row[];
  colors: string[];
  categories?: string[];
  series?: Series[];
  meta?: GaugeMeta;
}) {
  const { chartType, axisUnit, showValues, valueFormat } = item;
  if (item.queryMode === "closed_vs_target") {
    return <TargetActualBars rows={rows} meta={meta} showValues={showValues} colors={colors} />;
  }
  switch (chartType) {
    case "pie":
      return <PieChart rows={rows} colors={colors} showValues={showValues} valueFormat={valueFormat} />;
    case "doughnut":
      return <PieChart rows={rows} colors={colors} showValues={showValues} valueFormat={valueFormat} hollow />;
    case "line":
      return <LineChart rows={rows} colors={colors} axisUnit={axisUnit} showValues={showValues} categories={categories} series={series} />;
    case "area":
      return <LineChart rows={rows} colors={colors} axisUnit={axisUnit} showValues={showValues} filled categories={categories} series={series} />;
    case "number_trend":
      return <NumberTrend rows={rows} axisUnit={axisUnit} scalar={item.queryMode === "pipeline_total"} />;
    case "sparkline":
      return <Sparkline rows={rows} colors={colors} />;
    case "gauge":
      return <Gauge rows={rows} colors={colors} axisUnit={axisUnit} meta={meta} />;
    case "scatter":
      return <ScatterChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} />;
    case "bubble":
      return <BubbleChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} />;
    case "funnel":
      return <FunnelChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} />;
    case "waterfall":
      return <WaterfallChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} />;
    case "tornado":
      return <TornadoChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} />;
    case "heatmap_bar":
      return <HeatmapBar rows={rows} colors={colors} showValues={showValues} />;
    case "heatmap_grid":
      return <HeatmapGrid rows={rows} colors={colors} showValues={showValues} />;
    case "calendar":
      return <CalendarHeatmap rows={rows} colors={colors} />;
    case "word_cloud":
      return <WordCloud rows={rows} colors={colors} />;
    case "bar_horizontal":
      return <BarChart rows={rows} colors={colors} horizontal axisUnit={axisUnit} showValues={showValues} />;
    case "bar_clustered":
      return <ClusteredBarChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} mode="clustered" categories={categories} series={series} horizontal={item.orientation === "horizontal"} />;
    case "bar_stacked":
      return <ClusteredBarChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} mode="stacked" categories={categories} series={series} horizontal={item.orientation === "horizontal"} />;
    case "bar_double":
      return <ClusteredBarChart rows={rows} colors={colors} showValues={showValues} axisUnit={axisUnit} mode="double" categories={categories} series={series} horizontal={item.orientation === "horizontal"} />;
    case "bar":
    default:
      return <BarChart rows={rows} colors={colors} horizontal={item.orientation === "horizontal"} axisUnit={axisUnit} showValues={showValues} />;
  }
}

function ChartLegend({ rows, colors }: { rows: Row[]; colors: string[] }) {
  return (
    <ul className="dashboard-chart-legend">
      {rows.map((r, i) => (
        <li key={r.label} className="dashboard-chart-legend__item">
          <span className="dashboard-chart-legend__swatch" style={{ backgroundColor: colors[i % colors.length] }} />
          {prettyLabel(r.label)}
        </li>
      ))}
    </ul>
  );
}

function SeriesLegend({ series, colors }: { series: Series[]; colors: string[] }) {
  const symbol = useOrgMoneySymbol();
  return (
    <ul className="dashboard-chart-legend">
      {series.map((s, i) => (
        <li key={s.label} className="dashboard-chart-legend__item">
          <span className="dashboard-chart-legend__swatch" style={{ backgroundColor: colors[i % colors.length] }} />
          {orgMoneyLabel(prettyLabel(s.label), symbol)}
        </li>
      ))}
    </ul>
  );
}

function formatValue(
  value: number,
  total: number,
  showValues: boolean | undefined,
  valueFormat: DashboardChartItem["valueFormat"],
  symbol: string,
): string | null {
  if (!showValues) return null;
  if (valueFormat === "percent") return `${total ? Math.round((value / total) * 100) : 0}%`;
  if (valueFormat === "money") return prettyMoney(value, symbol);
  return prettyValue(value);
}

/** Target and Actual as separate horizontal bars, each a solid fill from the
 * chart's resolved colorScheme (colors[0] = target, colors[1] = actual) so
 * this chart type responds to palette changes like every other type. */
function TargetActualBars({ rows, meta, showValues, colors }: {
  rows: Row[];
  meta?: GaugeMeta;
  showValues?: boolean;
  colors: string[];
}) {
  const actual = meta?.actual ?? rows.find(r => /actual/i.test(r.label))?.value ?? 0;
  const target = meta?.target ?? rows.find(r => /target/i.test(r.label))?.value ?? 0;
  const max = Math.max(actual, target, 1);
  const actualWidth = (actual / max) * 72;
  const targetWidth = (target / max) * 72;
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  const targetColor = colors[0];
  const actualColor = colors[1 % colors.length];
  const symbol = useOrgMoneySymbol();
  return (
    <svg viewBox="0 0 100 56" className="dashboard-chart-svg" role="img" aria-label="Closed revenue versus target">
      <text x="2" y="14" className="dashboard-chart-svg__label">Target</text>
      <rect x="22" y="6" width={targetWidth} height="12" rx="2" style={{ fill: targetColor }} />
      {(showValues ?? true) && (
        <text x={24 + targetWidth} y="15" className="dashboard-chart-svg__value">{prettyMoney(target, symbol)}</text>
      )}
      <text x="2" y="38" className="dashboard-chart-svg__label">Actual</text>
      <rect x="22" y="30" width={actualWidth} height="12" rx="2" style={{ fill: actualColor }} />
      {(showValues ?? true) && (
        <text x={24 + actualWidth} y="39" className="dashboard-chart-svg__value">{prettyMoney(actual, symbol)}</text>
      )}
      <text x="22" y="54" className="dashboard-chart-svg__label">{pct}% of target{meta?.period_label ? ` · ${meta.period_label}` : ""}</text>
    </svg>
  );
}

function BarChart({ rows, colors, horizontal, axisUnit, showValues }: {
  rows: Row[]; colors: string[]; horizontal?: boolean; axisUnit?: string; showValues?: boolean;
}) {
  const symbol = useOrgMoneySymbol();
  const max = Math.max(...rows.map(r => r.value), 1);
  const barWidth = 100 / rows.length;
  if (horizontal) {
    const rowHeight = 60 / rows.length;
    return (
      <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Bar chart (horizontal)">
        {rows.map((r, i) => {
          const width = (r.value / max) * 80;
          return (
            <g key={r.label}>
              <rect
                x={18}
                y={i * rowHeight + rowHeight * 0.15}
                width={width}
                height={rowHeight * 0.7}
                rx={1}
                style={{ fill: colors[i % colors.length] }}
              />
              <text x={16} y={i * rowHeight + rowHeight / 2 + 1} textAnchor="end" className="dashboard-chart-svg__label">{prettyLabel(r.label)}</text>
              {showValues && (
                <text x={18 + width + 2} y={i * rowHeight + rowHeight / 2 + 1} className="dashboard-chart-svg__value">
                  {formatMarked(r.value, axisUnit, symbol)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Bar chart">
      {rows.map((r, i) => {
        const height = (r.value / max) * 50;
        return (
          <g key={r.label}>
            {showValues && (
              <text x={i * barWidth + barWidth / 2} y={53 - height} textAnchor="middle" className="dashboard-chart-svg__value">
                {formatMarked(r.value, axisUnit, symbol)}
              </text>
            )}
            <rect
              x={i * barWidth + barWidth * 0.15}
              y={55 - height}
              width={barWidth * 0.7}
              height={height}
              rx={1}
              style={{ fill: colors[i % colors.length] }}
            />
            <text x={i * barWidth + barWidth / 2} y={59} textAnchor="middle" className="dashboard-chart-svg__label">
              {prettyLabel(r.label)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Clustered / stacked / double-bar: when the API returns a multi-series pivot
 * (`categories` + `series`), render a true stacked (or clustered) chart.
 * Without series data, fall back to the honest single-series bar layout. */
function ClusteredBarChart({ rows, colors, showValues, axisUnit, mode, categories, series, horizontal }: {
  rows: Row[];
  colors: string[];
  showValues?: boolean;
  axisUnit?: string;
  mode: "clustered" | "stacked" | "double";
  categories?: string[];
  series?: Series[];
  horizontal?: boolean;
}) {
  if (series && series.length > 0 && categories && categories.length > 0 && mode === "stacked") {
    return (
      <StackedBarChart
        categories={categories}
        series={series}
        colors={colors}
        showValues={showValues}
        axisUnit={axisUnit}
        horizontal={horizontal}
      />
    );
  }
  return <BarChart rows={rows} colors={colors} axisUnit={axisUnit} showValues={showValues} horizontal={horizontal} />;
}

function StackedBarChart({ categories, series, colors, showValues, axisUnit, horizontal }: {
  categories: string[];
  series: Series[];
  colors: string[];
  showValues?: boolean;
  axisUnit?: string;
  horizontal?: boolean;
}) {
  const symbol = useOrgMoneySymbol();
  const totals = categories.map((_, index) => series.reduce((sum, s) => sum + (s.data[index] || 0), 0));
  const max = Math.max(...totals, 1);
  const formatMark = (value: number) => formatMarked(value, axisUnit, symbol);
  if (horizontal) {
    const vbW = 220;
    const vbH = Math.max(categories.length * 11, 44);
    const labelW = 38;
    const totalCol = 24;
    const barStart = labelW;
    const barMax = vbW - labelW - totalCol - 2;
    const rowHeight = vbH / Math.max(categories.length, 1);
    return (
      <svg
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        className="dashboard-chart-svg dashboard-chart-svg--wide-fill"
        role="img"
        aria-label="Stacked bar chart"
      >
        {categories.map((category, rowIndex) => {
          let cursor = barStart;
          const y = rowIndex * rowHeight + rowHeight * 0.12;
          const height = rowHeight * 0.76;
          return (
            <g key={category}>
              <text
                x={labelW - 2}
                y={rowIndex * rowHeight + rowHeight / 2 + 1}
                textAnchor="end"
                className="dashboard-chart-svg__label"
              >
                {prettyLabel(category)}
              </text>
              {series.map((s, seriesIndex) => {
                const value = s.data[rowIndex] || 0;
                const width = (value / max) * barMax;
                const x = cursor;
                cursor += width;
                if (width <= 0) return null;
                return (
                  <g key={`${category}-${s.label}`}>
                    <rect
                      x={x}
                      y={y}
                      width={width}
                      height={height}
                      rx={seriesIndex === 0 ? 1 : 0}
                      style={{ fill: colors[seriesIndex % colors.length] }}
                    />
                    {showValues && width >= 10 && (
                      <text
                        x={x + width / 2}
                        y={y + height / 2 + 1}
                        textAnchor="middle"
                        className="dashboard-chart-svg__value dashboard-chart-svg__label--on-fill"
                      >
                        {formatMark(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              {showValues && (
                <text
                  x={barStart + barMax + 2}
                  y={rowIndex * rowHeight + rowHeight / 2 + 1}
                  className="dashboard-chart-svg__value"
                >
                  {formatMark(totals[rowIndex])}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  }
  const barWidth = 100 / Math.max(categories.length, 1);
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Stacked bar chart">
      {categories.map((category, colIndex) => {
        let cursor = 55;
        const x = colIndex * barWidth + barWidth * 0.15;
        const width = barWidth * 0.7;
        return (
          <g key={category}>
            {series.map((s, seriesIndex) => {
              const value = s.data[colIndex] || 0;
              const height = (value / max) * 50;
              cursor -= height;
              if (height <= 0) return null;
              return (
                <g key={`${category}-${s.label}`}>
                  <rect
                    x={x}
                    y={cursor}
                    width={width}
                    height={height}
                    style={{ fill: colors[seriesIndex % colors.length] }}
                  />
                  {showValues && height >= 6 && (
                    <text x={x + width / 2} y={cursor + height / 2 + 1} textAnchor="middle" className="dashboard-chart-svg__value dashboard-chart-svg__label--on-fill">
                      {formatMark(value)}
                    </text>
                  )}
                </g>
              );
            })}
            <text x={colIndex * barWidth + barWidth / 2} y={59} textAnchor="middle" className="dashboard-chart-svg__label">
              {prettyLabel(category)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ rows, colors, axisUnit, showValues, filled, categories, series }: {
  rows: Row[];
  colors: string[];
  axisUnit?: string;
  showValues?: boolean;
  filled?: boolean;
  categories?: string[];
  series?: Series[];
}) {
  const symbol = useOrgMoneySymbol();
  if (series && series.length > 0 && categories && categories.length > 0) {
    return (
      <MultiLineChart
        categories={categories}
        series={series}
        colors={colors}
        showValues={showValues}
      />
    );
  }
  const max = Math.max(...rows.map(r => r.value), 1);
  const stepX = rows.length > 1 ? 100 / (rows.length - 1) : 0;
  const points = rows.map((r, i) => `${i * stepX},${50 - (r.value / max) * 45}`).join(" ");
  const areaPoints = filled ? `0,50 ${points} ${(rows.length - 1) * stepX},50` : "";
  return (
    <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet" className="dashboard-chart-svg" role="img" aria-label={filled ? "Area chart" : "Line chart"}>
      {filled && <polygon points={areaPoints} style={{ fill: colors[0], opacity: 0.25, stroke: "none" }} />}
      <polyline points={points} style={{ fill: "none", stroke: colors[0], strokeWidth: 1.5 }} />
      {rows.map((r, i) => (
        <g key={r.label}>
          <circle cx={i * stepX} cy={50 - (r.value / max) * 45} r={1.5} style={{ fill: colors[0] }} />
          {showValues && (
              <text x={i * stepX} y={50 - (r.value / max) * 45 - 3} textAnchor="middle" className="dashboard-chart-svg__value">
                {formatMarked(r.value, axisUnit, symbol)}
              </text>
          )}
        </g>
      ))}
    </svg>
  );
}

function monthTick(label: string): string {
  if (/^\d{4}-\d{2}/.test(label)) {
    const parsed = new Date(`${label.slice(0, 7)}-01T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    }
  }
  return prettyLabel(label);
}

function MultiLineChart({ categories, series, colors, showValues }: {
  categories: string[];
  series: Series[];
  colors: string[];
  showValues?: boolean;
}) {
  // Each series scales to its own max so money amounts and lead scores stay readable together.
  const symbol = useOrgMoneySymbol();
  const stepX = categories.length > 1 ? 100 / (categories.length - 1) : 0;
  const labelEvery = categories.length > 8 ? Math.ceil(categories.length / 6) : 1;
  const formatPoint = (value: number, seriesLabel: string) => {
    if (/\$|opportunit/i.test(seriesLabel)) return prettyMoney(value, symbol);
    return prettyValue(value);
  };
  return (
    <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet" className="dashboard-chart-svg" role="img" aria-label="Line chart">
      {series.map((s, seriesIndex) => {
        const max = Math.max(...s.data, 1);
        const points = categories.map((_, i) => {
          const value = s.data[i] || 0;
          return `${i * stepX},${50 - (value / max) * 42}`;
        }).join(" ");
        return (
          <g key={s.label}>
            <polyline points={points} style={{ fill: "none", stroke: colors[seriesIndex % colors.length], strokeWidth: 1.5 }} />
            {categories.map((category, i) => {
              const value = s.data[i] || 0;
              const x = i * stepX;
              const y = 50 - (value / max) * 42;
              return (
                <g key={`${s.label}-${category}`}>
                  <circle cx={x} cy={y} r={1.4} style={{ fill: colors[seriesIndex % colors.length] }} />
                  {showValues && value > 0 && (
                    <text x={x} y={y - 2.5 - seriesIndex * 3.5} textAnchor="middle" className="dashboard-chart-svg__value">
                      {formatPoint(value, s.label)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
      {categories.map((category, i) => (
        i % labelEvery === 0 || i === categories.length - 1 ? (
          <text key={`tick-${category}`} x={i * stepX} y={58} textAnchor="middle" className="dashboard-chart-svg__label">
            {monthTick(category)}
          </text>
        ) : null
      ))}
    </svg>
  );
}

function pieSlices(rows: Row[], colors: string[], radius: number, center: number) {
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1;
  let cursor = 0;
  return rows.map((r, i) => {
    const fraction = r.value / total;
    const startAngle = cursor * 2 * Math.PI;
    cursor += fraction;
    const endAngle = cursor * 2 * Math.PI;
    const midAngle = (startAngle + endAngle) / 2;
    const x1 = center + radius * Math.sin(startAngle);
    const y1 = center - radius * Math.cos(startAngle);
    const x2 = center + radius * Math.sin(endAngle);
    const y2 = center - radius * Math.cos(endAngle);
    const largeArc = fraction > 0.5 ? 1 : 0;
    const labelX = center + (radius * 0.65) * Math.sin(midAngle);
    const labelY = center - (radius * 0.65) * Math.cos(midAngle);
    return {
      path: `M${center},${center} L${x1},${y1} A${radius},${radius} 0 ${largeArc} 1 ${x2},${y2} Z`,
      color: colors[i % colors.length], label: r.label, value: r.value, labelX, labelY,
    };
  });
}

function PieChart({ rows, colors, showValues, valueFormat, hollow }: {
  rows: Row[]; colors: string[]; showValues?: boolean; valueFormat?: DashboardChartItem["valueFormat"]; hollow?: boolean;
}) {
  const symbol = useOrgMoneySymbol();
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1;
  const radius = 25;
  const center = 30;
  const slices = pieSlices(rows, colors, radius, center);
  return (
    <svg viewBox="0 0 60 60" preserveAspectRatio="xMidYMid meet" className="dashboard-chart-svg dashboard-chart-svg--pie" role="img" aria-label={hollow ? "Doughnut chart" : "Pie chart"}>
      {slices.map(s => <path key={s.label} d={s.path} style={{ fill: s.color }} />)}
      {hollow && <circle cx={center} cy={center} r={radius * 0.55} style={{ fill: "var(--color-panel)" }} />}
      {showValues && slices.map(s => (
        <text key={`${s.label}-value`} x={s.labelX} y={s.labelY} textAnchor="middle" className="dashboard-chart-svg__value dashboard-chart-svg__label--on-fill">
          {formatValue(s.value, total, showValues, valueFormat, symbol)}
        </text>
      ))}
    </svg>
  );
}

function NumberTrend({ rows, axisUnit, scalar }: { rows: Row[]; axisUnit?: string; scalar?: boolean }) {
  const symbol = useOrgMoneySymbol();
  const latest = rows[rows.length - 1];
  const previous = rows[rows.length - 2];
  const delta = !scalar && previous ? latest.value - previous.value : null;
  const display = formatMarked(latest.value, axisUnit, symbol);
  return (
    <div className={`dashboard-chart-trend${scalar ? " dashboard-chart-trend--hero" : ""}`}>
      <div className="dashboard-chart-trend__value">{display}</div>
      {delta !== null && (
        <div className={`dashboard-chart-trend__delta ${delta >= 0 ? "dashboard-chart-trend__delta--up" : "dashboard-chart-trend__delta--down"}`}>
          {delta >= 0 ? "▲" : "▼"} {isMoneyUnit(axisUnit) ? prettyMoney(Math.abs(delta), symbol) : prettyValue(Math.abs(delta))}
        </div>
      )}
    </div>
  );
}

/** No axis labels, no legend, meant for a KPI-adjacent inline context -- a
 * thin line with no marks, matching PlatformHub's own sparkline (axes
 * hidden, single series only). */
function Sparkline({ rows, colors }: { rows: Row[]; colors: string[] }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  const min = Math.min(...rows.map(r => r.value), 0);
  const range = max - min || 1;
  const stepX = rows.length > 1 ? 100 / (rows.length - 1) : 0;
  const points = rows.map((r, i) => `${i * stepX},${30 - ((r.value - min) / range) * 26}`).join(" ");
  return (
    <svg viewBox="0 0 100 30" className="dashboard-chart-svg dashboard-chart-svg--sparkline" role="img" aria-label="Sparkline">
      <polyline points={points} style={{ fill: "none", stroke: colors[0], strokeWidth: 1.5 }} />
    </svg>
  );
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

function gaugeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const large = startDeg - endDeg > 180 ? 1 : 0;
  return `M ${start[0]} ${start[1]} A ${r} ${r} 0 ${large} 1 ${end[0]} ${end[1]}`;
}

/** Share-of-total gauge by default; with targetMode (Actual/Target rows or
 * meta), renders closed revenue versus quota like the home-page quota gauge. */
function Gauge({ rows, colors, axisUnit, meta, targetMode }: {
  rows: Row[];
  colors: string[];
  axisUnit?: string;
  meta?: GaugeMeta;
  targetMode?: boolean;
}) {
  const symbol = useOrgMoneySymbol();
  const cx = 30, cy = 30, r = 22;
  if (targetMode || (rows.length === 2 && rows.some(r => /actual/i.test(r.label)) && rows.some(r => /target/i.test(r.label)))) {
    const actual = meta?.actual ?? rows.find(r => /actual/i.test(r.label))?.value ?? 0;
    const target = meta?.target ?? rows.find(r => /target/i.test(r.label))?.value ?? 0;
    const fraction = target > 0 ? Math.max(0, Math.min(1, actual / target)) : 0;
    const endDeg = 180 - fraction * 180;
    return (
      <svg viewBox="0 0 60 52" className="dashboard-chart-svg dashboard-chart-svg--gauge" role="img" aria-label="Closed revenue versus target">
        <path
          d={gaugeArc(cx, cy, r, 180, 0)}
          style={{ fill: "none", stroke: "var(--color-border-soft)", strokeWidth: 5, strokeLinecap: "round" }}
        />
        {fraction > 0 && (
          <path
            d={gaugeArc(cx, cy, r, 180, Math.max(endDeg, 1))}
            data-gauge-fill="true"
            style={{ fill: "none", stroke: colors[0], strokeWidth: 5, strokeLinecap: "round" }}
          />
        )}
        <text x={cx} y={cy - 1} textAnchor="middle" className="dashboard-chart-svg__value">
          {Math.round(fraction * 100)}%
        </text>
        <text x={cx} y={cy + 8} textAnchor="middle" className="dashboard-chart-svg__label">
          Actual {prettyMoney(actual, symbol)}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" className="dashboard-chart-svg__label">
          Target {prettyMoney(target, symbol)}
        </text>
      </svg>
    );
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1;
  const top = [...rows].sort((a, b) => b.value - a.value)[0];
  const fraction = Math.max(0, Math.min(1, (top?.value ?? 0) / total));
  const endDeg = 180 - fraction * 180;
  const pct = Math.round(fraction * 100);
  return (
    <svg viewBox="0 0 60 46" className="dashboard-chart-svg dashboard-chart-svg--gauge" role="img" aria-label="Gauge">
      <path
        d={gaugeArc(cx, cy, r, 180, 0)}
        style={{ fill: "none", stroke: "var(--color-border-soft)", strokeWidth: 5, strokeLinecap: "round" }}
      />
      {fraction > 0 && (
        <path
          d={gaugeArc(cx, cy, r, 180, Math.max(endDeg, 1))}
          data-gauge-fill="true"
          style={{ fill: "none", stroke: colors[0], strokeWidth: 5, strokeLinecap: "round" }}
        />
      )}
      <text x={cx} y={cy - 2} textAnchor="middle" className="dashboard-chart-svg__value">
        {pct}%{axisUnit ? ` ${isMoneyUnit(axisUnit) ? symbol : axisUnit}` : ""}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" className="dashboard-chart-svg__label">
        {prettyLabel(top?.label ?? "")}
      </text>
    </svg>
  );
}

/** Scatter with no natural x/y pairing in {label,value}[] row data --
 * PlatformHub's own scatter has the identical limitation (it re-indexes
 * values as {x: index, y: value}), so this is a faithful port of that same
 * simplification: x is the row's position, y is its value. */
function ScatterChart({ rows, colors, showValues, axisUnit }: { rows: Row[]; colors: string[]; showValues?: boolean; axisUnit?: string }) {
  const symbol = useOrgMoneySymbol();
  const max = Math.max(...rows.map(r => r.value), 1);
  const stepX = rows.length > 1 ? 90 / (rows.length - 1) : 0;
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Scatter chart">
      {rows.map((r, i) => {
        const x = 5 + i * stepX;
        const y = 50 - (r.value / max) * 45;
        return (
          <g key={r.label}>
            <circle cx={x} cy={y} r={2} style={{ fill: colors[i % colors.length] }} />
            {showValues && <text x={x} y={y - 3} textAnchor="middle" className="dashboard-chart-svg__value">{formatMarked(r.value, axisUnit, symbol)}</text>}
            <text x={x} y={57} textAnchor="middle" className="dashboard-chart-svg__label">{prettyLabel(r.label)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Same index-as-x placement as ScatterChart (same data-shape limitation),
 * with circle radius scaled by value -- PlatformHub's own bubble chart does
 * the same radius-from-value derivation, just against a genuine y value from
 * its richer data model instead of also reusing that value for y position. */
function BubbleChart({ rows, colors, showValues, axisUnit }: { rows: Row[]; colors: string[]; showValues?: boolean; axisUnit?: string }) {
  const symbol = useOrgMoneySymbol();
  const max = Math.max(...rows.map(r => r.value), 1);
  const stepX = rows.length > 1 ? 90 / (rows.length - 1) : 0;
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Bubble chart">
      {rows.map((r, i) => {
        const x = 5 + i * stepX;
        const y = 50 - (r.value / max) * 45;
        const radius = 1.5 + (r.value / max) * 6;
        return (
          <g key={r.label}>
            <circle cx={x} cy={y} r={radius} style={{ fill: colors[i % colors.length], opacity: 0.75 }} />
            {showValues && <text x={x} y={y - radius - 1} textAnchor="middle" className="dashboard-chart-svg__value">{formatMarked(r.value, axisUnit, symbol)}</text>}
            <text x={x} y={57} textAnchor="middle" className="dashboard-chart-svg__label">{prettyLabel(r.label)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Pipeline-order trapezoids. Band width is the deal count (or other
 * aggregation) in that stage — not a decorative 7→1 sequence. Known CRM
 * stages stay in sales order; anything else falls back to size-descending. */
const PIPELINE_STAGE_ORDER = ["lead_in", "qualifying", "proposal", "negotiation", "closed_won", "closed_lost"];

function FunnelChart({ rows, colors, showValues, axisUnit }: { rows: Row[]; colors: string[]; showValues?: boolean; axisUnit?: string }) {
  const symbol = useOrgMoneySymbol();
  const known = rows.some(r => PIPELINE_STAGE_ORDER.includes(r.label));
  const sorted = [...rows].sort((a, b) => {
    if (known) {
      const ai = PIPELINE_STAGE_ORDER.indexOf(a.label);
      const bi = PIPELINE_STAGE_ORDER.indexOf(b.label);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    }
    return b.value - a.value;
  });
  const max = Math.max(...sorted.map(r => r.value), 1);
  const rowHeight = 50 / sorted.length;
  const unit = axisUnit || "deals";
  return (
    <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet" className="dashboard-chart-svg" role="img" aria-label="Funnel chart">
      {sorted.map((r, i) => {
        const topWidth = Math.max((r.value / max) * 90, 18);
        const nextValue = sorted[i + 1]?.value ?? r.value;
        const bottomWidth = Math.max((nextValue / max) * 90, 18);
        const y = i * rowHeight + 2;
        const cx = 50;
        const points = [
          [cx - topWidth / 2, y], [cx + topWidth / 2, y],
          [cx + bottomWidth / 2, y + rowHeight - 1], [cx - bottomWidth / 2, y + rowHeight - 1],
        ].map(p => p.join(",")).join(" ");
        return (
          <g key={r.label}>
            <polygon points={points} style={{ fill: colors[i % colors.length] }} />
            <text x={cx} y={y + rowHeight / 2} textAnchor="middle" className="dashboard-chart-svg__label dashboard-chart-svg__label--on-fill">
              <tspan x={cx} dy="-0.35em">{prettyLabel(r.label)}</tspan>
              {showValues !== false && (
                <tspan x={cx} dy="1.35em">{isMoneyUnit(unit) ? prettyMoney(r.value, symbol) : `${prettyValue(r.value)} ${r.value === 1 && unit === "deals" ? "deal" : unit}`}</tspan>
              )}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Cumulative running-total bars, some rising, some falling. Sign
 * convention (documented, since {label,value}[] rows carry no inherent
 * sign): every row's value is treated as a DELTA applied in row order
 * starting from 0 -- a positive value draws a bar rising from the prior
 * cumulative total, a negative value draws one falling from it, matching
 * PlatformHub's own waterfall (computed from a running cumulative total
 * over `values` with up/down coloring by sign). This means a chart whose
 * underlying aggregation only ever produces non-negative values (e.g.
 * COUNT) renders as a monotonically-rising staircase, which is the
 * mathematically correct waterfall for all-positive deltas, not a bug. */
function WaterfallChart({ rows, colors, showValues, axisUnit }: { rows: Row[]; colors: string[]; showValues?: boolean; axisUnit?: string }) {
  const symbol = useOrgMoneySymbol();
  let running = 0;
  const bars = rows.map(r => {
    const start = running;
    running += r.value;
    return { label: r.label, start, end: running, value: r.value };
  });
  const allValues = bars.flatMap(b => [b.start, b.end]);
  const max = Math.max(...allValues, 1);
  const min = Math.min(...allValues, 0);
  const range = max - min || 1;
  const barWidth = 100 / bars.length;
  const toY = (v: number) => 55 - ((v - min) / range) * 50;
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Waterfall chart">
      {bars.map((b, i) => {
        const up = b.end >= b.start;
        const y = toY(Math.max(b.start, b.end));
        const height = Math.abs(toY(b.start) - toY(b.end));
        return (
          <g key={b.label}>
            <rect x={i * barWidth + barWidth * 0.15} y={y} width={barWidth * 0.7} height={Math.max(height, 0.5)}
              style={{ fill: up ? colors[0] : colors[1 % colors.length] }} />
            {showValues && (
              <text x={i * barWidth + barWidth / 2} y={y - 1} textAnchor="middle" className="dashboard-chart-svg__value">
                {isMoneyUnit(axisUnit)
                  ? `${b.value >= 0 ? "+" : "-"}${prettyMoney(Math.abs(b.value), symbol)}`
                  : `${b.value >= 0 ? "+" : ""}${prettyValue(b.value)}${axisUnit ? ` ${axisUnit}` : ""}`}
              </text>
            )}
            <text x={i * barWidth + barWidth / 2} y={59} textAnchor="middle" className="dashboard-chart-svg__label">{prettyLabel(b.label)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Horizontal diverging bars from a center axis. PlatformHub's own tornado
 * is genuinely two-series (a second dataset negated to diverge from a zero
 * centerline) -- with only one series available here, every bar diverges
 * from the same center by its own single value (alternating left/right by
 * row parity purely for the visual "tornado" fan shape), which is an
 * honest single-series stand-in, not a real two-series comparison. */
function TornadoChart({ rows, colors, showValues, axisUnit }: { rows: Row[]; colors: string[]; showValues?: boolean; axisUnit?: string }) {
  const symbol = useOrgMoneySymbol();
  const max = Math.max(...rows.map(r => r.value), 1);
  const rowHeight = 60 / rows.length;
  const cx = 50;
  return (
    <svg viewBox="0 0 100 60" className="dashboard-chart-svg" role="img" aria-label="Tornado chart">
      <line x1={cx} y1={0} x2={cx} y2={60} style={{ stroke: "var(--color-border-soft)", strokeWidth: 0.5 }} />
      {rows.map((r, i) => {
        const width = (r.value / max) * 45;
        const left = i % 2 === 0;
        const x = left ? cx - width : cx;
        return (
          <g key={r.label}>
            <rect x={x} y={i * rowHeight + rowHeight * 0.15} width={width} height={rowHeight * 0.7} style={{ fill: colors[i % colors.length] }} />
            <text x={left ? cx - width - 2 : cx + width + 2} y={i * rowHeight + rowHeight / 2 + 1}
              textAnchor={left ? "end" : "start"} className="dashboard-chart-svg__label">{prettyLabel(r.label)}</text>
            {showValues && (
              <text x={cx} y={i * rowHeight + rowHeight / 2 + 1} textAnchor="middle" className="dashboard-chart-svg__value">
                {formatMarked(r.value, axisUnit, symbol)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Colour-intensity cells. PlatformHub's own bar-based heatmap supports an
 * optional multi-series dataset (falling back to single `values` otherwise)
 * -- with only single-series data available here, this always renders the
 * single-row fallback: one strip of cells, one per category, shaded by
 * value/max. A real multi-row bar-based heatmap needs the same pivoted
 * multi-series query the clustered/stacked bar types need (see file-level
 * comment). */
function HeatmapBar({ rows, colors, showValues }: { rows: Row[]; colors: string[]; showValues?: boolean }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  const cellWidth = 100 / rows.length;
  return (
    <svg viewBox="0 0 100 24" className="dashboard-chart-svg dashboard-chart-svg--heatmap" role="img" aria-label="Heatmap (bar-based)">
      {rows.map((r, i) => {
        const intensity = Math.max(0.15, r.value / max);
        return (
          <g key={r.label}>
            <rect x={i * cellWidth + 0.5} y={0} width={cellWidth - 1} height={16} style={{ fill: colors[0], opacity: intensity }} />
            {showValues && <text x={i * cellWidth + cellWidth / 2} y={10} textAnchor="middle" className="dashboard-chart-svg__value">{prettyValue(r.value)}</text>}
            <text x={i * cellWidth + cellWidth / 2} y={22} textAnchor="middle" className="dashboard-chart-svg__label">{prettyLabel(r.label)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** True 2D heatmap grid in PlatformHub (rows = category labels, columns =
 * a second "cluster by" field's values) -- structurally impossible to
 * reproduce faithfully from single-series {label,value}[] rows, since there
 * is no second dimension to lay out as columns. Honest single-series
 * fallback: a 1-row grid, one cell per category, shaded by value/max --
 * this is a real 2D CSS grid container, just with a height of 1, so the
 * genuine 2D case can be added later purely by feeding it a 2D array once
 * a pivoted query exists, with no markup change needed. */
function HeatmapGrid({ rows, colors, showValues }: { rows: Row[]; colors: string[]; showValues?: boolean }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div className="dashboard-chart-heatmap-grid" style={{ gridTemplateColumns: `repeat(${rows.length}, 1fr)` }}>
      {rows.map(r => {
        const intensity = Math.max(0.15, r.value / max);
        return (
          <div key={r.label} className="dashboard-chart-heatmap-grid__cell" style={{ backgroundColor: colors[0], opacity: intensity }}
            title={`${r.label}: ${r.value}`}>
            {showValues && <span className="dashboard-chart-heatmap-grid__value">{prettyValue(r.value)}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Day-cell heatmap. PlatformHub buckets a date-grouped {labels: dates,
 * values: counts} response into a month calendar. This platform's own
 * chart-data response has no guaranteed date structure -- `label` is
 * whatever the "group by" field produced, which is a date string ONLY when
 * the admin picked a date field to group by. Best-effort implementation:
 * if every row's label parses as a valid date, render real calendar day
 * cells (grouped by month, one column strip per month) shaded by value;
 * otherwise fall back to treating each row as one flat cell in label order
 * (labeled explicitly as "not date-labeled" so this limitation is visible
 * in the UI, not silently wrong). */
function parseDay(label: string): Date | null {
  const match = label.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(label);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function CalendarHeatmap({ rows, colors }: { rows: Row[]; colors: string[] }) {
  const parsed = rows.map(r => ({ ...r, date: parseDay(r.label) }));
  const allValid = parsed.every(r => r.date);
  const max = Math.max(...rows.map(r => r.value), 1);
  if (!allValid) {
    return (
      <div className="dashboard-chart-calendar dashboard-chart-calendar--fallback">
        <p className="dashboard-chart-card__empty">Group by a date field to see a calendar view. Showing values in category order:</p>
        <div className="dashboard-chart-heatmap-grid" style={{ gridTemplateColumns: `repeat(${rows.length}, 1fr)` }}>
          {rows.map(r => (
            <div key={r.label} className="dashboard-chart-heatmap-grid__cell" style={{ backgroundColor: colors[0], opacity: Math.max(0.15, r.value / max) }} title={`${prettyLabel(r.label)}: ${prettyValue(r.value)}`} />
          ))}
        </div>
      </div>
    );
  }
  const byMonth = new Map<string, typeof parsed>();
  for (const row of parsed) {
    const date = row.date as Date;
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const list = byMonth.get(key) ?? [];
    list.push(row);
    byMonth.set(key, list);
  }
  const [year, month] = [...byMonth.entries()].sort((a, b) => b[1].length - a[1].length)[0][0].split("-").map(Number);
  const values = new Map<number, number>();
  for (const row of byMonth.get(`${year}-${month}`) ?? []) {
    values.set((row.date as Date).getDate(), row.value);
  }
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ day: number | null; value: number }> = [
    ...Array.from({ length: firstWeekday }, () => ({ day: null, value: 0 })),
    ...Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, value: values.get(i + 1) ?? 0 })),
  ];
  const monthLabel = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  return (
    <div className="dashboard-chart-calendar-wrap">
      <p className="dashboard-chart-card__axis-title">{monthLabel}</p>
      <div className="dashboard-chart-calendar">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
          <div key={`${day}-${i}`} className="dashboard-chart-calendar__dow">{day}</div>
        ))}
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`dashboard-chart-calendar__cell${cell.day ? "" : " dashboard-chart-calendar__cell--empty"}${cell.value ? " dashboard-chart-calendar__cell--hot" : ""}`}
            style={cell.day ? {
              backgroundColor: cell.value ? colors[0] : "var(--color-canvas-raised)",
              opacity: cell.value ? Math.max(0.4, cell.value / max) : 1,
            } : undefined}
            title={cell.day ? `${monthLabel} ${cell.day}: ${prettyValue(cell.value)}` : undefined}
          >
            {cell.day ? <span className="dashboard-chart-calendar__day">{cell.day}</span> : null}
            {cell.day && cell.value > 0 ? (
              <span className="dashboard-chart-calendar__value">{prettyValue(cell.value)}</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Font-size-by-value text layout. PlatformHub's own word cloud is also a
 * simple sized-label layout (not a real collision-avoided word-cloud
 * packing algorithm) -- this is a faithful port of that same simplicity,
 * using a flex/wrap of labels sized by sqrt(value/max), matching
 * PlatformHub's own sizing formula. */
function WordCloud({ rows, colors }: { rows: Row[]; colors: string[] }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div className="dashboard-chart-wordcloud">
      {rows.map((r, i) => {
        const scale = Math.sqrt(r.value / max);
        return (
          <span
            key={r.label}
            className="dashboard-chart-wordcloud__word"
            style={{ fontSize: `calc(var(--font-size-sm) + ${scale} * 1.5rem)`, color: colors[i % colors.length] }}
            title={String(r.value)}
          >
            {prettyLabel(r.label)}
          </span>
        );
      })}
    </div>
  );
}
